import type { MfgPricingLayerBand, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import {
  DEFAULT_SHELF_ALERT_PCT,
  shelfCostIsIncomplete,
  shelfVariance,
  shelfVarianceAttention,
  shelfVarianceAttentionTitle,
  type ShelfVarianceAttention,
  type ShelfVarianceTone,
} from "@/lib/manufacturing/cogsShelfVariance";
import { formatUsd } from "./pricingGridFormat";

export const SHELF_VARIANCE_TONE: Record<ShelfVarianceTone, string> = {
  up: "text-amber-800 dark:text-amber-200",
  down: "text-emerald-800 dark:text-emerald-200",
  flat: "text-gray-500 dark:text-zinc-400",
  missing: "text-gray-400 dark:text-zinc-500",
};

export const SHELF_ATTENTION_TONE: Record<Exclude<ShelfVarianceAttention, "quiet">, string> = {
  alert: "font-semibold text-red-700 dark:text-red-300",
  warning: "text-orange-700 dark:text-orange-300",
};

function bandTotal(row: MfgPricingLayerGridRow, band: MfgPricingLayerBand): number {
  if (band === "cogs") return row.cogs_total_usd;
  if (band === "wholesale") return row.wholesale_total_usd;
  return row.msrp_total_usd;
}

export function PricingBandTotalCell({
  band,
  row,
  onOpen,
  alertThresholdPct = DEFAULT_SHELF_ALERT_PCT,
}: {
  band: MfgPricingLayerBand;
  row: MfgPricingLayerGridRow;
  onOpen?: () => void;
  alertThresholdPct?: number;
}) {
  const total = bandTotal(row, band);
  const shelf = band === "wholesale" ? row.shelf_wholesale_usd : band === "msrp" ? row.shelf_msrp_usd : null;
  const variance = band === "cogs" ? null : shelfVariance(total, shelf);
  const attention = variance
    ? shelfVarianceAttention(variance, shelfCostIsIncomplete(row), alertThresholdPct)
    : "quiet";
  const noteClass = variance
    ? `text-[10px] leading-tight ${attention === "quiet" ? `font-normal ${SHELF_VARIANCE_TONE[variance.tone]}` : SHELF_ATTENTION_TONE[attention]}`
    : "";
  const title = variance ? shelfVarianceAttentionTitle(attention, variance, alertThresholdPct) : "";
  return (
    <div className="text-right">
      <div>{formatUsd(total)}</div>
      {variance && onOpen ? (
        <button type="button" className={`${noteClass} underline decoration-dotted`} title={title} onClick={onOpen}>
          {variance.label}
        </button>
      ) : variance ? (
        <div className={noteClass} title={title}>
          {variance.label}
        </div>
      ) : null}
    </div>
  );
}
